import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit, Trash2, Copy, History, Eye, EyeOff, Lock, Globe, Building2, Clock, User, Folder, Tag, RotateCcw } from 'lucide-react';
import { diffWords, diffLines } from 'diff';
import { documentationAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import ArticleContent from '../../components/documentation/ArticleContent';
import RelatedItems from '../../components/documentation/RelatedItems';
import Attachments from '../../components/documentation/Attachments';
import { isInternalRole } from '../../utils/roles.js';

// Visibility badge component
function VisibilityBadge({ visibility }) {
  const config = {
    private: { icon: Lock, label: 'Internal Only', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    company: { icon: Building2, label: 'Company Shared', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    public: { icon: Globe, label: 'Public', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  };
  const { icon: Icon, label, className } = config[visibility] || config.private;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${className}`}>
      <Icon className="w-4 h-4" />
      {label}
    </span>
  );
}

// Status badge component
function StatusBadge({ status }) {
  const config = {
    draft: { label: 'Draft', className: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300' },
    published: { label: 'Published', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    archived: { label: 'Archived', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  };
  const { label, className } = config[status] || config.draft;

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${className}`}>
      {label}
    </span>
  );
}

// Diff rendering helper component
function DiffView({ oldText, newText, type = 'lines' }) {
  const diff = useMemo(() => {
    const oldStr = oldText || '';
    const newStr = newText || '';
    return type === 'words' ? diffWords(oldStr, newStr) : diffLines(oldStr, newStr);
  }, [oldText, newText, type]);

  if (!oldText && !newText) {
    return <span className="text-neutral-500 italic">No content</span>;
  }

  // Check if there are any changes
  const hasChanges = diff.some(part => part.added || part.removed);
  if (!hasChanges) {
    return <span className="text-neutral-500 italic">No changes</span>;
  }

  return (
    <div className={type === 'lines' ? 'font-mono text-sm whitespace-pre-wrap' : ''}>
      {diff.map((part, i) => {
        if (part.added) {
          return (
            <span
              key={i}
              className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300"
            >
              {part.value}
            </span>
          );
        }
        if (part.removed) {
          return (
            <span
              key={i}
              className="bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 line-through"
            >
              {part.value}
            </span>
          );
        }
        return <span key={i}>{part.value}</span>;
      })}
    </div>
  );
}

// Version history modal with diff comparison
function VersionHistoryModal({ article, versions, onRestore, onClose, isRestoring }) {
  const [selectedVersion, setSelectedVersion] = useState(
    versions && versions.length > 0 ? versions[0] : null
  );

  const sortedVersions = useMemo(() => {
    if (!versions) return [];
    return [...versions].sort((a, b) => b.version - a.version);
  }, [versions]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Version History</h2>
          <button
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - Two Panel Layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Version List */}
          <div className="w-72 border-r border-neutral-200 dark:border-neutral-700 overflow-y-auto flex-shrink-0">
            {/* Current Version */}
            <div className="p-3 bg-brand-50 dark:bg-brand-900/20 border-b border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-brand-500"></span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  Current (v{article.version})
                </span>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 ml-4">
                {new Date(article.updated_at).toLocaleDateString()}
              </p>
            </div>

            {/* Past Versions */}
            {sortedVersions.length > 0 ? (
              <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {sortedVersions.map((version) => (
                  <button
                    key={version.id}
                    onClick={() => setSelectedVersion(version)}
                    className={`w-full p-3 text-left transition-colors ${
                      selectedVersion?.id === version.id
                        ? 'bg-neutral-100 dark:bg-neutral-700'
                        : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${
                        selectedVersion?.id === version.id
                          ? 'bg-brand-500'
                          : 'bg-neutral-300 dark:bg-neutral-600'
                      }`}></span>
                      <span className="font-medium text-neutral-900 dark:text-white">
                        Version {version.version}
                      </span>
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 ml-4">
                      {new Date(version.created_at).toLocaleDateString()}
                    </p>
                    {version.change_summary && (
                      <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1 ml-4 truncate">
                        "{version.change_summary}"
                      </p>
                    )}
                    <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1 ml-4">
                      by {version.created_by_email || 'Unknown'}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-neutral-500 dark:text-neutral-400">
                No previous versions
              </div>
            )}
          </div>

          {/* Right Panel - Diff View */}
          <div className="flex-1 overflow-y-auto">
            {selectedVersion ? (
              <div className="p-4">
                {/* Comparison Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-neutral-600 dark:text-neutral-400">
                    Comparing <span className="font-semibold text-neutral-900 dark:text-white">v{selectedVersion.version}</span>
                    {' → '}
                    <span className="font-semibold text-neutral-900 dark:text-white">Current (v{article.version})</span>
                  </div>
                  <button
                    onClick={() => onRestore(selectedVersion.id)}
                    disabled={isRestoring}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 text-white rounded-lg transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Restore v{selectedVersion.version}
                  </button>
                </div>

                {/* Title Diff */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Title
                  </h4>
                  <div className="p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700">
                    <DiffView
                      oldText={selectedVersion.title}
                      newText={article.title}
                      type="words"
                    />
                  </div>
                </div>

                {/* Content Diff */}
                <div>
                  <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Content (Markdown)
                  </h4>
                  <div className="p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700 max-h-[400px] overflow-y-auto">
                    <DiffView
                      oldText={selectedVersion.content_markdown}
                      newText={article.content_markdown}
                      type="lines"
                    />
                  </div>
                </div>

                {/* Change Summary if exists */}
                {selectedVersion.change_summary && (
                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <span className="font-medium">Change note:</span> {selectedVersion.change_summary}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-neutral-500 dark:text-neutral-400">
                Select a version to compare
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900">
          <div className="flex items-center justify-between">
            <div className="text-sm text-neutral-500 dark:text-neutral-400">
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 bg-green-100 dark:bg-green-900/40 border border-green-300 dark:border-green-700 rounded"></span>
                Added
              </span>
              <span className="inline-flex items-center gap-1 ml-4">
                <span className="w-3 h-3 bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700 rounded"></span>
                Removed
              </span>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ArticleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [showVersions, setShowVersions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isInternal = isInternalRole(user?.role);
  const isAdmin = user?.role === 'admin';

  // Fetch article with versions
  const { data, isLoading, error } = useQuery({
    // The editor caches the same article without versions under the bare key.
    queryKey: ['documentation', 'article', id, { includeVersions: isInternal }],
    queryFn: () => documentationAPI.getArticle(id, { includeVersions: isInternal }),
  });

  const article = data?.data?.article;

  // Publish/unpublish mutation
  const publishMutation = useMutation({
    mutationFn: (publish) => publish
      ? documentationAPI.publishArticle(id)
      : documentationAPI.unpublishArticle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'article', id] });
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: () => documentationAPI.duplicateArticle(id),
    onSuccess: (response) => {
      navigate(`/documentation/articles/${response.data.article.id}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (hard) => documentationAPI.deleteArticle(id, hard),
    onSuccess: () => {
      navigate('/documentation');
    },
  });

  // Restore version mutation
  const restoreVersionMutation = useMutation({
    mutationFn: (versionId) => documentationAPI.restoreArticleVersion(id, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'article', id] });
      setShowVersions(false);
    },
  });

  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <div className="mb-6">
          <div className="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-64 animate-pulse" />
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <div className="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-1/2 mb-4 animate-pulse" />
          <div className="space-y-2">
            <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-full animate-pulse" />
            <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4 animate-pulse" />
            <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-5/6 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="animate-fade-in">
        <div className="text-center py-12">
          <h2 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">Article Not Found</h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            The article you're looking for doesn't exist or you don't have access to it.
          </p>
          <Link
            to="/documentation"
            className="inline-flex items-center gap-2 text-brand-600 dark:text-brand-400 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Documentation
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Back link */}
      <Link
        to="/documentation"
        className="inline-flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Documentation
      </Link>

      {/* Header */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 mb-6">
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-3">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3">
                {isInternal && <VisibilityBadge visibility={article.visibility} />}
                {isInternal && <StatusBadge status={article.status} />}
                {article.is_template && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                    Template
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            {isInternal && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowVersions(true)}
                  className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  title="Version History"
                >
                  <History className="w-5 h-5" />
                </button>
                <button
                  onClick={() => duplicateMutation.mutate()}
                  disabled={duplicateMutation.isPending}
                  className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  title="Duplicate"
                >
                  <Copy className="w-5 h-5" />
                </button>
                {article.status === 'published' ? (
                  <button
                    onClick={() => publishMutation.mutate(false)}
                    disabled={publishMutation.isPending}
                    className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                    title="Unpublish"
                  >
                    <EyeOff className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => publishMutation.mutate(true)}
                    disabled={publishMutation.isPending}
                    className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                    title="Publish"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                )}
                <Link
                  to={`/documentation/articles/${id}/edit`}
                  className="p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit className="w-5 h-5" />
                </Link>
                {isAdmin && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-neutral-500 dark:text-neutral-400">
            {article.folder_name && (
              <span className="flex items-center gap-1.5">
                <Folder className="w-4 h-4" />
                {article.folder_name}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              Updated {new Date(article.updated_at).toLocaleDateString()}
            </span>
            {article.updated_by_email && (
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {article.updated_by_email}
              </span>
            )}
            {isInternal && (
              <span className="text-neutral-400">
                v{article.version}
              </span>
            )}
          </div>

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <Tag className="w-4 h-4 text-neutral-400" />
              {article.tags.map((tag, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded text-sm text-neutral-700 dark:text-neutral-300"
                  style={tag.color ? { backgroundColor: `${tag.color}20`, color: tag.color } : undefined}
                >
                  {typeof tag === 'string' ? tag : tag.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        {article.excerpt && (
          <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-6 pb-6 border-b border-neutral-200 dark:border-neutral-700">
            {article.excerpt}
          </p>
        )}
        <ArticleContent content={article.content} />
      </div>

      {/* Related Items */}
      <div className="mt-6">
        <RelatedItems
          sourceType="article"
          sourceId={id}
          companyId={article.company_id}
        />
      </div>

      {/* Attachments */}
      <div className="mt-6">
        <Attachments
          assetType="article"
          assetId={id}
          companyId={article.company_id}
        />
      </div>

      {/* Version History Modal */}
      {showVersions && (
        <VersionHistoryModal
          article={article}
          versions={article.versions}
          onRestore={(versionId) => restoreVersionMutation.mutate(versionId)}
          onClose={() => setShowVersions(false)}
          isRestoring={restoreVersionMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
              Delete Article?
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
              Are you sure you want to delete "{article.title}"? This will archive the article.
              {isAdmin && ' You can also permanently delete it.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(false)}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
              >
                Archive
              </button>
              {isAdmin && (
                <button
                  onClick={() => deleteMutation.mutate(true)}
                  disabled={deleteMutation.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Delete Forever
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
